/**
 * v1 station-snapshot zod schema.
 *
 * Mirrors `data/snapshot-format.yaml` — that file is the source of truth.
 * The schema validates the wire-level JSON; domain entities are constructed
 * downstream by `importSnapshot`.
 *
 * Notes on the mapping (wire → domain):
 *  - `chargingProfiles[].schedule.startSchedule` and `validFrom`/`validTo`
 *    are ISO date-time strings on the wire but epoch-ms numbers in the
 *    domain. `importSnapshot` does the conversion.
 *  - `offlineQueue[]` does not carry `queueId` on the wire; we generate one
 *    at import time (the queueId is a local-only handle).
 *  - `name` is the station's display name; the domain calls it
 *    `displayName`. The exporter writes it under `name` to match the YAML.
 */
import { z } from 'zod';
import {
  AuthStatus,
  ChargingRateUnit,
  ConfigurationKeyType,
  ConfigurationSection,
  OcppVersion,
  ProfileKind,
  ProfilePurpose,
  RecurrencyKind,
  StopReason,
} from '@/domain/types';

const isoDateTime = z.string().datetime({ offset: true });

const cpid = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_\-:.]+$/, 'cpid must match [A-Za-z0-9_\\-:.]+');

const evseSchema = z.object({
  id: z.number().int().min(1),
  name: z.string().min(1),
  maxPowerW: z.number().positive(),
  connectorIds: z.array(z.number().int().min(1)).min(1),
});

const topologySchema = z.object({
  cpMaxPowerW: z.number().min(0),
  evses: z.array(evseSchema).min(1),
});

const configurationKeySchema = z.object({
  key: z.string().min(1),
  value: z.string(),
  readonly: z.boolean(),
  type: z.enum(
    Object.values(ConfigurationKeyType) as [
      ConfigurationKeyType,
      ...ConfigurationKeyType[],
    ],
  ),
  section: z.enum(
    Object.values(ConfigurationSection) as [
      ConfigurationSection,
      ...ConfigurationSection[],
    ],
  ),
});

const chargingSchedulePeriodSchema = z.object({
  startPeriod: z.number().int().min(0),
  limit: z.number().min(0),
  numberPhases: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
});

const chargingScheduleSchema = z.object({
  startSchedule: isoDateTime.optional(),
  duration: z.number().int().min(0).optional(),
  chargingRateUnit: z.enum(
    Object.values(ChargingRateUnit) as [ChargingRateUnit, ...ChargingRateUnit[]],
  ),
  minChargingRate: z.number().min(0).optional(),
  chargingSchedulePeriod: z.array(chargingSchedulePeriodSchema).min(1),
});

const chargingProfileSchema = z.object({
  id: z.number().int(),
  connectorId: z.number().int().min(0),
  purpose: z.enum(
    Object.values(ProfilePurpose) as [ProfilePurpose, ...ProfilePurpose[]],
  ),
  stackLevel: z.number().int().min(0),
  kind: z.enum(Object.values(ProfileKind) as [ProfileKind, ...ProfileKind[]]),
  recurrencyKind: z
    .enum(Object.values(RecurrencyKind) as [RecurrencyKind, ...RecurrencyKind[]])
    .optional(),
  validFrom: isoDateTime.optional(),
  validTo: isoDateTime.optional(),
  transactionId: z.number().int().optional(),
  schedule: chargingScheduleSchema,
});

const offlineSessionSchema = z.object({
  idTag: z.string().min(1),
  connectorId: z.number().int().min(1),
  startedAt: isoDateTime,
  duration: z.number().int().min(0),
  meterStartWh: z.number().min(0),
  energyDeliveredWh: z.number().min(0),
  meterValueIntervalSec: z.number().int().min(1),
  startSoC: z.number().min(0).max(100).optional(),
  endSoC: z.number().min(0).max(100).optional(),
  voltage: z.number().positive(),
  stopReason: z.enum(Object.values(StopReason) as [StopReason, ...StopReason[]]),
});

// v2 (snapshot completeness) — the 2.0.1 Device Model and the Local Auth List
// ARE station config; v1 silently dropped them so exporting a 2.0.1 station
// lost its config. Both are optional so v1 snapshots still validate.
const variableValueSchema = z.object({
  component: z.string().min(1),
  variable: z.string().min(1),
  evseId: z.number().int().min(0),
  connectorId: z.number().int().min(0),
  attributeType: z.string().min(1),   // Actual | Target | MinSet | MaxSet
  value: z.string(),
  mutability: z.string().min(1),      // ReadOnly | ReadWrite | WriteOnly
  dataType: z.string().min(1),
  unit: z.string().optional(),
  section: z.string().min(1),
});

const localAuthEntrySchema = z.object({
  idTag: z.string().min(1),
  status: z.enum(Object.values(AuthStatus) as [AuthStatus, ...AuthStatus[]]),
  expiryDate: z.string().optional(),
  parentIdTag: z.string().optional(),
});

const localAuthListSchema = z.object({
  version: z.number().int().min(0),
  entries: z.array(localAuthEntrySchema),
});

const stationSchema = z.object({
  cpid,
  name: z.string().min(1).max(128),
  wsUrl: z.string().url(),
  ocppVersion: z.enum(
    Object.values(OcppVersion) as [OcppVersion, ...OcppVersion[]],
  ),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  topology: topologySchema,
  configuration: z.array(configurationKeySchema),
  chargingProfiles: z.array(chargingProfileSchema),
  offlineQueue: z.array(offlineSessionSchema),
  // v2 — optional so v1 imports unchanged.
  variableValues: z.array(variableValueSchema).optional(),
  localAuthList: localAuthListSchema.optional(),
});

export const snapshotV1Schema = z.object({
  schemaVersion: z.union([z.literal(1), z.literal(2)]),
  exportedAt: isoDateTime,
  exportedBy: z.string().min(1),
  station: stationSchema,
});

export type SnapshotV1 = z.infer<typeof snapshotV1Schema>;
export type SnapshotStationV1 = z.infer<typeof stationSchema>;
export type SnapshotConfigurationKeyV1 = z.infer<typeof configurationKeySchema>;
export type SnapshotChargingProfileV1 = z.infer<typeof chargingProfileSchema>;
export type SnapshotOfflineSessionV1 = z.infer<typeof offlineSessionSchema>;
export type SnapshotTopologyV1 = z.infer<typeof topologySchema>;
export type SnapshotEVSEV1 = z.infer<typeof evseSchema>;
export type SnapshotVariableValueV2 = z.infer<typeof variableValueSchema>;
export type SnapshotLocalAuthEntryV2 = z.infer<typeof localAuthEntrySchema>;
export type SnapshotLocalAuthListV2 = z.infer<typeof localAuthListSchema>;

/**
 * Parse + validate a JSON value as a v1 snapshot. Returns either a typed
 * snapshot or a list of human-readable validation errors. Never throws.
 */
export function parseSnapshotV1(
  input: unknown,
): { readonly ok: true; readonly value: SnapshotV1 } | { readonly ok: false; readonly errors: readonly string[] } {
  const r = snapshotV1Schema.safeParse(input);
  if (r.success) return { ok: true, value: r.data };
  const errors = r.error.issues.map((iss) => {
    const path = iss.path.length > 0 ? iss.path.join('.') : '(root)';
    return `${path}: ${iss.message}`;
  });
  return { ok: false, errors };
}
